import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { patientService, PatientListFilters } from '../patientService';
import { DuplicateCheckParams } from '../../../types/patient';

export function usePatients(page = 1, limit = 20, filters?: PatientListFilters) {
  return useQuery({
    queryKey: ['patients', page, limit, filters],
    queryFn: () => patientService.getPatients(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function usePatient(id: string) {
  return useQuery({
    queryKey: ['patients', id],
    queryFn: () => patientService.getPatient(id),
    enabled: !!id,
  });
}

export function useCreatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: patientService.createPatient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => patientService.updatePatient(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['patients', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

export function useSearchPatients(query: string, includeInactive = false) {
  return useQuery({
    queryKey: ['patients', 'search', query, includeInactive],
    queryFn: () => patientService.searchPatients(query, includeInactive),
    placeholderData: (previous) => previous,
    enabled: query.length >= 2,
    staleTime: 10000,
  });
}

export function useCheckDuplicate(params: DuplicateCheckParams, enabled = false) {
  return useQuery({
    queryKey: ['patients', 'duplicate', params],
    queryFn: () => patientService.checkDuplicate(params),
    enabled,
    staleTime: 0,
  });
}

export function useDeletePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => patientService.deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useBulkDeletePatients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientService.bulkDeletePatients(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useTrashedPatients(page = 1, limit = 20, filters?: PatientListFilters) {
  return useQuery({
    queryKey: ['patients', 'trash', page, limit, filters],
    queryFn: () => patientService.getTrashedPatients(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useRestorePatients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientService.restorePatients(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function usePermanentlyDeletePatients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientService.permanentlyDeletePatients(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
    },
  });
}

export function useForceDeletePatients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => patientService.forceDeletePatients(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
